import axiosInstance from './axiosInstance';
import appInsights from '../services/appInsights';

// Create a component-specific logger for media service
const logger = appInsights.getComponentLogger('MediaService');
/**
 * Service for interacting with media files API
 */
const mediaService = {
  /**
   * Get all media files
   * @returns {Promise<Array>} - List of media files
   */
  getAllMediaFiles: async () => {
    try {
      logger.info('Fetching all media files from API...', { operation: 'get-all' });
      const response = await axiosInstance.get('/media-files');
      logger.info('Media files fetched successfully', { 
        operation: 'get-all-success',
        count: response.data.length 
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      logger.error('Failed to fetch media files', { 
        operation: 'get-all-error',
        error: errorMessage,
        status: error.response?.status,
        url: '/media-files'
      });
      
      throw new Error(`Failed to fetch media files: ${errorMessage}`);
    }
  },

  /**
   * Get a specific media file by ID
   * @param {string} id - Unique ID of the media file
   * @returns {Promise<Object>} - Media file details
   */
  getMediaFileById: async (id) => {
    if (!id) {
      logger.error('Media file ID is required', { operation: 'get-by-id-error' });
      throw new Error('Media file ID is required');
    }
    
    try {
      logger.info('Fetching media file by ID', { operation: 'get-by-id', id });
      const response = await axiosInstance.get(`/media-files/${encodeURIComponent(id)}`);
      logger.info('Media file fetched successfully', { 
        operation: 'get-by-id-success',
        id,
        type: response.data.type,
        size: response.data.size
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      logger.error(`Failed to fetch media file`, {
        operation: 'get-by-id-error',
        id,
        error: errorMessage,
        status: error.response?.status
      });
      
      throw new Error(`Failed to fetch media file: ${errorMessage}`);
    }
  },
  /**
   * Get a secure streaming URL for a media file
   * @param {string} id - Unique ID of the media file
   * @returns {Promise<Object>} - Object containing the streaming URL and content type
   */
  getStreamingUrl: async (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    try {
      console.log(`Requesting streaming URL for media file with ID ${id}...`);
      const response = await axiosInstance.get(`/media-files/${encodeURIComponent(id)}/stream`);
      
      // Validate response contains necessary data
      if (!response.data || !response.data.streamingUrl) {
        console.error('Invalid streaming URL response:', response.data);
        throw new Error('Server returned an invalid streaming URL');
      }
      
      console.log('Streaming URL obtained successfully:', {
        name: response.data.name,
        contentType: response.data.contentType,
        fileType: response.data.fileType,
        timestamp: response.data.timestamp,
        urlStart: response.data.streamingUrl.substring(0, 50) + '...'
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error getting streaming URL for media file with ID ${id}:`, error);
      
      // Provide detailed error information
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      throw new Error(`Failed to get streaming URL: ${errorMessage}`);
    }
  },  /**
   * Upload a new media file
   * @param {File} file - The file to upload
   * @param {string} name - Name of the file
   * @param {string} description - Description of the file
   * @returns {Promise<Object>} - The saved media file
   */
  uploadMediaFile: async (file, name, description) => {
    // Validate required parameters
    if (!file) throw new Error('File is required');
    if (!name) throw new Error('File name is required');
    
    // Validate file properties
    if (!(file instanceof File)) {
      throw new Error('Invalid file object. Must be an instance of File.');
    }
    
    if (file.size === 0) {
      throw new Error('File is empty (size is 0 bytes)');
    }
    
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      throw new Error(`File is too large (${file.size} bytes). Maximum size is ${maxSize} bytes (100MB).`);
    }
    
    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'video/mp4', 'application/mp4', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.type}. Only MP3 and MP4 files are allowed.`);
    }

    // Detect corporate proxy environment for optimal configuration.
    // Keep this strict to avoid false positives in normal HTTPS deployments.
    const currentlyThroughProxy = window.location.href.includes('shield.ericomcloud.net') ||
                                 window.location.origin.includes('ericomcloud.net') ||
                                 navigator.userAgent.includes('Ericom');

    const hasProxyIndicators = document.referrer.includes('ericomcloud.net');

    const isCorporateProxy = currentlyThroughProxy || hasProxyIndicators;
      // Check if this is an MP4 file that should use base64 upload in proxy environments
    const isMP4File = file.type.includes('video') || file.type.includes('mp4');
      if (isCorporateProxy && isMP4File) {
      const fileSizeMB = file.size / 1024 / 1024;
      const estimatedBase64SizeMB = fileSizeMB * 1.33; // Base64 increases size by ~33%
      
      // Only attempt base64 upload for smaller files to avoid Azure Container Apps limits
      // Azure Container Apps appears to have ~20-25MB request limit despite our 150MB Express setting
      if (estimatedBase64SizeMB <= 20) {
        console.log('🎬 MP4 file detected in corporate proxy environment - using base64 upload method');
        try {
          return await mediaService.uploadMediaFileBase64(file, name, description);
        } catch (base64Error) {
          console.warn('⚠️ Base64 upload failed, attempting fallback to FormData:', base64Error.message);
          
          // If base64 upload fails (e.g., file too large, server error), fall back to FormData
          if (base64Error.message.includes('413') || 
              base64Error.message.includes('too large') ||
              base64Error.message.includes('Request failed with status code 413')) {
            console.log('🔄 Falling back to FormData upload due to size or server constraints');
            // Continue with FormData upload below
          } else {
            // Re-throw other errors
            throw base64Error;
          }
        }
      } else {
        console.log(`📊 MP4 file too large for base64 upload (${estimatedBase64SizeMB.toFixed(1)}MB estimated), using FormData directly`);
        // Continue with FormData upload below
      }
    }

    // Continue with regular FormData upload for non-proxy environments or MP3 files
    
    try {
      // Log detailed file information
      console.log('Preparing to upload file:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });
      
      // Create form data
      const formData = new FormData();
      
      // Add file with explicit filename
      formData.append('file', file, file.name);
      formData.append('name', name);
      formData.append('description', description || '');

      // Log FormData contents (for debugging)
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        if (pair[0] === 'file') {
          console.log('file entry:', {
            fieldName: pair[0],
            fileName: pair[1].name,
            fileType: pair[1].type,
            fileSize: pair[1].size
          });
        } else {
          console.log(`${pair[0]}:`, pair[1]);
        }      }      // Detect corporate proxy environment for optimal configuration
      // Enhanced detection for production environments
      const currentlyThroughProxy = window.location.href.includes('shield.ericomcloud.net') ||
                                   window.location.origin.includes('ericomcloud.net') ||
                                   navigator.userAgent.includes('Ericom');

      const hasProxyIndicators = document.referrer.includes('ericomcloud.net');

      const isCorporateProxy = currentlyThroughProxy || hasProxyIndicators;
      
      if (isCorporateProxy) {
        console.log('🔍 Corporate proxy detected - using proxy-optimized upload configuration');
        console.log('Proxy detection details:', {
          currentlyThroughProxy,
          hasProxyIndicators,
          hostname: window.location.hostname,
          environment: process.env.NODE_ENV,
          userAgent: navigator.userAgent.substring(0, 100)
        });
      }      // Configure the request with proxy-friendly settings
      const config = {
        headers: {
          // Explicitly remove Content-Type to let browser set it with boundary for FormData
          'Content-Type': undefined
        },
        // Override axios instance defaults for file uploads
        withCredentials: false, // Disable credentials for file uploads to avoid CORS issues
        // Increase redirects for proxy scenarios
        maxRedirects: isCorporateProxy ? 15 : 10,
        // Increase timeout for proxy scenarios
        timeout: isCorporateProxy ? 360000 : 240000, // 6 minutes for proxy, 4 minutes normal
        // Validate status codes
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress:`, {
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percent: percentCompleted
          });
        }
      };

      console.log('Starting upload with config:', {
        url: '/media-files',
        headers: config.headers,
        withCredentials: config.withCredentials,
        fileSize: file.size,
        isFormData: true
      });      const response = await axiosInstance.post('/media-files', formData, config);
      
      // Validate response - check if we got an HTML page from Ericom Shield instead of JSON
      if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
        console.error('Received HTML response instead of JSON - likely Ericom Shield proxy interference:', {
          responseType: typeof response.data,
          responseStart: response.data.substring(0, 200),
          status: response.status,
          headers: response.headers
        });        // Check if it's specifically an Ericom Shield page
        if (response.data.includes('Ericom Shield') || response.data.includes('ericomcloud.net')) {
          console.log('Ericom Shield intercepted response - but upload reached 100% with 200 OK status');
          console.log('Upload indicators suggest success:', {
            uploadProgress: '100%',
            httpStatus: response.status,
            statusText: response.statusText,
            fileSize: file.size,
            fileName: name
          });
          
          // Since we got 100% upload progress and 200 OK, the upload likely succeeded
          // Try verification, but if that fails too, assume success
          try {
            // Wait a moment for the backend to finish processing
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Check upload status using the file name
            const statusResponse = await axiosInstance.get(`/media-files/upload-status/${encodeURIComponent(name)}`);
            
            console.log('Status verification response:', {
              status: statusResponse.status,
              statusText: statusResponse.statusText,
              data: statusResponse.data,
              headers: statusResponse.headers,
              dataType: typeof statusResponse.data
            });
            
            // Check if we got HTML again (proxy blocking status endpoint too)
            if (typeof statusResponse.data === 'string' && statusResponse.data.includes('<!DOCTYPE html>')) {
              console.warn('Status endpoint also blocked by proxy - assuming upload succeeded');
              
              // Since the main upload got 100% progress and 200 OK, assume it worked
              return {
                name: name,
                fileType: file.type.includes('audio') ? 'mp3' : 'mp4',
                fileSize: file.size,
                uniqueId: 'proxy-upload-' + Date.now(), // Temporary ID
                createdAt: new Date().toISOString(),
                description: description || '',
                contentType: file.type,
                proxyWorkaround: true,
                assumedSuccess: true // Flag indicating we're assuming success
              };
            }
            
            if (statusResponse.data && statusResponse.data.success) {
              console.log('Upload verification successful:', statusResponse.data);
              
              // Return the file info from status check
              return {
                name: statusResponse.data.name,
                fileType: statusResponse.data.type,
                fileSize: statusResponse.data.size,
                uniqueId: statusResponse.data.id,
                createdAt: statusResponse.data.uploaded,
                description: description || '',
                contentType: file.type,
                proxyWorkaround: true // Flag to indicate this was a proxy workaround
              };
            } else {
              console.warn('Status check returned success=false, but upload may have worked. Response:', statusResponse.data);
              
              // If we got a proper response but success=false, still try to return useful info
              // Since the upload reached 100% and returned 200 OK, it likely succeeded
              return {
                name: name,
                fileType: file.type.includes('audio') ? 'mp3' : 'mp4',
                fileSize: file.size,
                uniqueId: 'proxy-upload-' + Date.now(),
                createdAt: new Date().toISOString(),
                description: description || '',
                contentType: file.type,
                proxyWorkaround: true,
                statusCheckFailed: true,
                statusResponse: statusResponse.data
              };
            }
          } catch (statusError) {
            console.error('Status verification failed:', statusError);
              // Since we got 100% upload progress and 200 OK status, assume the upload worked
            // The proxy is just blocking our verification attempts
            console.log('Assuming upload succeeded based on 100% progress and 200 OK status');
            
            return {
              name: name,
              fileType: file.type.includes('audio') ? 'mp3' : 'mp4',
              fileSize: file.size,
              uniqueId: 'proxy-upload-' + Date.now(),
              createdAt: new Date().toISOString(),
              description: description || '',
              contentType: file.type,
              proxyWorkaround: true,
              verificationFailed: true,              successMessage: 'Upload completed successfully! Due to corporate proxy settings, please refresh the page to see your file in the list.'
            };
          }
        } else {
          throw new Error(
            'Upload request was intercepted by a security proxy or firewall.\n' +
            'The request reached the server but the response was blocked or modified.\n' +
            'Please contact your IT team for assistance.'
          );
        }
      }
      
      // Validate that we have proper JSON response
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Server returned invalid response format');
      }
      
      console.log('Upload successful:', {
        responseStatus: response.status,
        responseData: response.data,
        originalFileSize: file.size,
        savedFileSize: response.data.fileSize
      });
      
      // For proxy environments, we might not get the exact file size back due to processing
      // So we'll be more lenient with size validation in corporate proxy environments
      if (isCorporateProxy) {
        console.log('Corporate proxy environment detected - skipping strict file size validation');
        if (!response.data.id && !response.data.name) {
          console.warn('Response missing expected file properties, but proceeding due to proxy environment');
        }
      } else {
        // Verify the saved file size matches the uploaded size (non-proxy environments only)
        if (response.data.fileSize !== file.size) {
          console.error('File size mismatch:', {
            uploaded: file.size,
            saved: response.data.fileSize
          });
          throw new Error('Uploaded file size does not match saved file size');
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error uploading media file:', error);
        // Enhanced error handling for corporate proxies and CORS issues
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('Server error details:', {
          status,
          data: errorData,
          headers: error.response.headers
        });
        
        if (status === 400) {
          throw new Error(`Invalid upload request: ${errorData.error || 'Bad request'}`);
        } else if (status === 413) {
          throw new Error('File is too large. Maximum file size is 100MB.');
        } else if (status === 0 || status === 'null') {
          // This often happens with CORS or proxy issues
          throw new Error('Upload blocked by security proxy or CORS policy. Contact your IT department.');
        } else {
          throw new Error(`Upload failed (${status}): ${errorData?.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        
        // Check if this is likely a CORS/proxy issue
        const errorText = error.request.responseText || '';
        if (errorText.includes('CORS') || error.message.includes('CORS')) {
          throw new Error('Request blocked by CORS policy. This may be due to a corporate security proxy.');        } else if (
          window.location.href.includes('shield.ericomcloud.net') || 
          error.request.responseURL?.includes('shield.ericomcloud.net') ||
          navigator.userAgent.includes('Ericom') ||
          (error.message && error.message.includes('shield'))
        ) {
          throw new Error(
            'Request blocked by Ericom Shield security proxy. Try one of the following:\n' +
            '1. Connect from a network without corporate proxy\n' +
            '2. Request your IT team to allow this domain in the CORS policy\n' +
            '3. Use the Azure Storage Explorer desktop application instead'
          );
        } else {
          throw new Error('No response received from server. Check your network connection.');
        }
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  },
  /**
   * Upload a media file using base64 encoding (proxy-compatible method)
   * @param {File} file - The file to upload
   * @param {string} name - Name of the file
   * @param {string} description - Description of the file
   * @returns {Promise<Object>} - The saved media file
   */
  uploadMediaFileBase64: async (file, name, description) => {
    try {
      // Log detailed file information
      console.log('🔄 Preparing base64 upload for proxy compatibility:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });

      // Convert file to base64
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      console.log('📖 File converted to base64, length:', fileData.length);

      // Prepare request payload
      const payload = {
        name,
        description: description || '',
        fileData,
        fileName: file.name,
        mimetype: file.type,
        fileSize: file.size
      };

      // Configure request for proxy environments
      const config = {
        headers: {
          'Content-Type': 'application/json',
          // Remove problematic headers that might cause CORS issues
        },
        withCredentials: false,
        timeout: 300000, // 5 minutes for large base64 uploads
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      };

      console.log('🚀 Starting base64 upload to /media-files/upload-base64');

      const response = await axiosInstance.post('/media-files/upload-base64', payload, config);

      // Validate response
      if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
        console.error('Received HTML response instead of JSON - proxy interference detected');
        
        // If Ericom Shield intercepted the response but we got a 200 status, assume success
        if (response.status === 200 && response.data.includes('Ericom Shield')) {
          console.log('✅ Base64 upload likely succeeded despite proxy interception');
          
          return {
            name: name,
            fileType: file.type.includes('audio') ? 'mp3' : 'mp4',
            fileSize: file.size,
            uniqueId: 'base64-upload-' + Date.now(),
            createdAt: new Date().toISOString(),
            description: description || '',
            contentType: file.type,
            uploadMethod: 'base64',
            proxyWorkaround: true,
            successMessage: 'MP4 upload completed successfully! Please refresh the page to see your file in the list.'
          };
        } else {
          throw new Error('Upload request was intercepted by security proxy');
        }
      }

      console.log('✅ Base64 upload successful:', {
        responseStatus: response.status,
        uploadMethod: response.data.uploadMethod,
        fileName: response.data.name,
        fileSize: response.data.fileSize
      });

      return response.data;
    } catch (error) {
      console.error('❌ Base64 upload failed:', error);

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('Base64 upload server error:', {
          status,
          data: errorData,
          headers: error.response.headers
        });
        if (status === 413) {
          // Azure Container Apps platform limit hit - this should trigger fallback
          const estimatedBase64Size = (file.size * 1.33); // Base64 increases size by ~33%
          const errorMessage = `Request too large for Azure Container Apps (413). Original file: ${(file.size / 1024 / 1024).toFixed(1)}MB, Base64 encoded: ${(estimatedBase64Size / 1024 / 1024).toFixed(1)}MB. Platform limit appears to be ~20-25MB.`;
          console.error('🚫', errorMessage);
          throw new Error(errorMessage);
        } else if (status === 400) {
          throw new Error(`Invalid base64 upload request: ${errorData.error || errorData.details || 'Bad request'}`);        } else if (status === 413) {
          const estimatedBase64Size = (file.size * 1.33); // Base64 increases size by ~33%
          throw new Error(`File is too large for base64 upload. Try using a smaller file or connect from a non-proxy network. Original file size: ${(file.size / 1024 / 1024).toFixed(1)}MB, Estimated base64 size: ${(estimatedBase64Size / 1024 / 1024).toFixed(1)}MB.`);
        } else {
          throw new Error(`Base64 upload failed (${status}): ${errorData?.error || errorData?.details || 'Unknown server error'}`);
        }
      } else if (error.request) {
        console.error('No response received for base64 upload:', error.request);
        
        if (error.message.includes('timeout')) {
          throw new Error('Base64 upload timed out. This may happen with very large files in proxy environments.');
        } else {
          throw new Error('No response received from server during base64 upload. Check your network connection.');
        }
      } else {
        throw new Error(`Base64 upload failed: ${error.message}`);
      }
    }
  },
  /**
   * Delete a media file
   * @param {string} id - Unique ID of the media file to delete
   * @returns {Promise<Object>} - Delete result
   */
  deleteMediaFile: async (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    try {
      console.log(`Deleting media file with ID: ${id}...`);
      const response = await axiosInstance.delete(`/media-files/${encodeURIComponent(id)}`);
      console.log('File deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error deleting media file with ID ${id}:`, error);
      
      // Provide detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 404) {
          throw new Error('Media file not found. It may have already been deleted.');
        } else {
          throw new Error(`Deletion failed (${status}): ${errorData.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Check your network connection.');
      } else {
        throw new Error(`Deletion error: ${error.message}`);
      }
    }
  },

  /**
   * Get a direct streaming URL for a media file
   * This uses the server-side streaming endpoint which avoids range request issues
   * @param {string} id - Unique ID of the media file
   * @returns {string} - The direct streaming URL
   */
  getDirectStreamingUrl: (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    const baseUrl = axiosInstance.defaults.baseURL || '';
    // Add timestamp to prevent caching
    const timestamp = Date.now();
    const url = `${baseUrl}/media-files/${encodeURIComponent(id)}/direct-stream?t=${timestamp}`;
    console.log('Generated direct streaming URL:', url);
    return url;
  },

  /**
   * Get a proxy streaming URL for a media file
   * This uses the server-side proxy-stream endpoint which downloads the full file from Azure
   * before sending it to the client, avoiding range request issues completely
   * @param {string} id - Unique ID of the media file
   * @returns {string} - The proxy streaming URL
   */
  getProxyStreamingUrl: (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    const baseUrl = axiosInstance.defaults.baseURL || '';
    // Add noRange=true parameter to explicitly tell the server not to use range requests
    // Also add a timestamp to prevent caching issues
    const timestamp = Date.now();
    const url = `${baseUrl}/media-files/${encodeURIComponent(id)}/proxy-stream?noRange=true&t=${timestamp}`;
    console.log('Generated proxy streaming URL:', url);
    return url;
  },

  /**
   * Get a direct download URL for a media file
   * This endpoint serves the file as a download attachment rather than streaming
   * Useful when streaming doesn't work properly
   * @param {string} id - Unique ID of the media file
   * @returns {string} - The download URL
   */
  getDownloadUrl: (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    const baseUrl = axiosInstance.defaults.baseURL || '';
    // Add timestamp to prevent caching
    const timestamp = Date.now();
    const url = `${baseUrl}/media-files/${encodeURIComponent(id)}/download?t=${timestamp}`;
    console.log('Generated download URL:', url);
    return url;
  },

  /**
   * Get a binary streaming URL for a media file
   * This endpoint is optimized specifically for handling format errors
   * @param {string} id - Unique ID of the media file
   * @returns {string} - The binary streaming URL
   */
  getBinaryStreamingUrl: (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    const baseUrl = axiosInstance.defaults.baseURL || '';
    // Add timestamp to prevent caching
    const timestamp = Date.now();
    const url = `${baseUrl}/media-files/${encodeURIComponent(id)}/direct-stream?t=${timestamp}`;
    console.log('Generated binary streaming URL (using direct-stream):', url);
    return url;
  },

  /**
   * Get a blob-direct streaming URL for a media file
   * This returns a direct Azure Blob Storage SAS URL that bypasses the backend streaming
   * @param {string} id - Unique ID of the media file
   * @returns {Promise<Object>} - Object containing the direct streaming URL and metadata
   */
  getBlobDirectStreamingUrl: async (id) => {
    if (!id) throw new Error('Media file ID is required');
    
    try {
      console.log(`Getting blob-direct streaming URL for media file with ID ${id}...`);
      const response = await axiosInstance.get(`/media-files/${encodeURIComponent(id)}/blob-direct`);
      
      // Validate response contains necessary data
      if (!response.data || !response.data.streamingUrl) {
        console.error('Invalid blob-direct streaming URL response:', response.data);
        throw new Error('Server returned an invalid streaming URL');
      }
      
      console.log('Blob-direct streaming URL obtained successfully:', {
        name: response.data.name,
        contentType: response.data.contentType,
        fileType: response.data.fileType,
        method: response.data.method,
        expiresIn: response.data.expiresIn,
        urlStart: response.data.streamingUrl.substring(0, 50) + '...'
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error getting blob-direct streaming URL for media file with ID ${id}:`, error);
      
      // Provide detailed error information
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      throw new Error(`Failed to get blob-direct streaming URL: ${errorMessage}`);
    }
  },

  /**
   * Download QR code for a media file
   * @param {string} id - Unique ID of the media file
   * @param {string} fileName - Optional custom filename for the download
   * @returns {Promise<void>} - Initiates download in browser
   */
  downloadQRCode: async (id, fileName) => {
    if (!id) throw new Error('Media file ID is required');
    
    try {
      console.log(`Downloading QR code for media file with ID: ${id}...`);
      
      const response = await axiosInstance.get(`/media-files/${encodeURIComponent(id)}/qr-code`, {
        responseType: 'blob', // Important: get response as blob for file download
        timeout: 30000 // 30 seconds timeout
      });
      
      console.log('QR code downloaded successfully:', {
        size: response.data.size,
        type: response.data.type
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ? `${fileName}-qr-code.png` : `qr-code-${id}.png`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('QR code download initiated successfully');
      return;
    } catch (error) {
      console.error(`Error downloading QR code for media file with ID ${id}:`, error);
      
      // Provide detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 404) {
          throw new Error('Media file not found or QR code could not be generated.');
        } else if (status === 500) {
          throw new Error('Server error generating or retrieving QR code. Please try again.');
        } else {
          throw new Error(`QR code download failed (${status}): ${errorData?.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Check your network connection.');
      } else {
        throw new Error(`QR code download error: ${error.message}`);
      }
    }
  },

  /**
   * Get QR code data URL for display purposes
   * @param {string} id - Unique ID of the media file
   * @returns {Promise<Object>} - QR code data with dataUrl and frontendBaseUrl
   */
  getQRCodeDataURL: async (id) => {
    try {
      if (!id) throw new Error('Media file ID is required');

      console.log(`Getting QR code data URL for media file with ID: ${id}...`);
      
      const response = await axiosInstance.get(`/media-files/${encodeURIComponent(id)}/qr-data-url`);
      
      if (!response.data || !response.data.dataUrl) {
        throw new Error('Server returned invalid QR code data URL response');
      }
      
      console.log('QR code data URL obtained successfully');
      return response.data; // Return the full response object instead of just dataUrl
      
    } catch (error) {
      console.error(`Error getting QR code data URL for media file with ID ${id}:`, error);
      
      if (error.response) {
        const { status, data: errorData } = error.response;
        if (status === 404) {
          throw new Error('Media file not found or QR code could not be generated.');
        } else if (status >= 500) {
          throw new Error('Server error generating QR code data URL. Please try again.');
        } else {
          throw new Error(`QR code data URL failed (${status}): ${errorData?.error || 'Unknown server error'}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server. Check your network connection.');
      } else {
        throw new Error(`QR code data URL error: ${error.message}`);
      }
    }
  },

  /**
   * Update media file description
   * @param {string} id - Unique ID of the media file
   * @param {string} description - New description for the file
   * @returns {Promise<Object>} - Updated media file details
   */
  updateMediaFileDescription: async (id, description) => {
    try {
      if (!id) {
        logger.error('Media file ID is required for update', { operation: 'update-description-error' });
        throw new Error('Media file ID is required');
      }

      logger.info('Updating media file description', { 
        operation: 'update-description',
        id: id,
        descriptionLength: description?.length || 0
      });

      const response = await axiosInstance.patch(`/media-files/${encodeURIComponent(id)}`, {
        description: description || ''
      });

      logger.info('Media file description updated successfully', { 
        operation: 'update-description-success',
        id: id
      });

      return response.data;
    } catch (error) {
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      logger.error('Failed to update media file description', { 
        operation: 'update-description-error',
        id: id,
        error: errorMessage,
        status: error.response?.status
      });
      
      throw new Error(`Failed to update media file description: ${errorMessage}`);
    }
  },

  /**
   * Replace a media file while keeping the same ID and QR code
   * @param {string} id - Unique ID of the media file to replace
   * @param {File} file - New file to replace with
   * @returns {Promise<Object>} - Updated media file details
   */
  replaceMediaFile: async (id, file) => {
    if (!id) {
      logger.error('Media file ID is required for replacement', { operation: 'replace-file-error' });
      throw new Error('Media file ID is required for replacement');
    }
    
    if (!file) {
      logger.error('New file is required for replacement', { operation: 'replace-file-error' });
      throw new Error('New file is required for replacement');
    }

    try {
      logger.info('Replacing media file', {
        operation: 'replace-file',
        fileId: id,
        newFileName: file.name,
        newFileSize: file.size,
        newFileType: file.type
      });

      const formData = new FormData();
      formData.append('file', file);

      // Enhanced corporate proxy environment detection - use POST instead of PUT for better compatibility
      
      const url = window.location.href.toLowerCase();
      const proxyUrlPatterns = ['shield.ericomcloud.net', 'proxy.', 'gateway.', 'secure.', 'corp.', 'internal.', '.proxy.', 'firewall'];
      const hasProxyUrl = proxyUrlPatterns.some(pattern => url.includes(pattern));
      
      const userAgent = navigator.userAgent.toLowerCase();
      const proxySignatures = ['ericom', 'proxy', 'gateway', 'corporate', 'enterprise', 'security'];
      const hasProxySignature = proxySignatures.some(sig => userAgent.includes(sig));
      
      const hostname = window.location.hostname;
      const hasCorporateIndicators = hostname.includes('.internal') || hostname.includes('.corp') || 
                                    hostname.includes('.local') || hostname.match(/^10\./) ||
                                    hostname.match(/^172\.(1[6-9]|2\d|3[01])\./) || hostname.match(/^192\.168\./);
      
      const isCorporateProxy = hasProxyUrl || hasProxySignature || hasCorporateIndicators;

      const httpMethod = isCorporateProxy ? 'post' : 'put';
      const endpoint = `/media-files/${encodeURIComponent(id)}/replace`;

      logger.info('Using HTTP method for replace operation', {
        operation: 'replace-file-method',
        method: httpMethod.toUpperCase(),
        corporateProxy: isCorporateProxy,
        fileId: id
      });

      const response = await axiosInstance[httpMethod](endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: isCorporateProxy ? 360000 : 240000, // 6 minutes for proxy, 4 minutes normal
      });

      logger.info('Media file replaced successfully', { 
        operation: 'replace-file-success',
        fileId: id,
        newFileName: file.name,
        method: httpMethod.toUpperCase()
      });

      return response.data;
    } catch (error) {
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}` 
        : error.request 
          ? 'No response received from server' 
          : `Request error: ${error.message}`;
      
      logger.error('Failed to replace media file', { 
        operation: 'replace-file-error',
        fileId: id,
        error: errorMessage,
        status: error.response?.status
      });
      
      throw new Error(`Failed to replace media file: ${errorMessage}`);
    }
  },
};

export default mediaService;
